import React from 'react';
import { Box, Typography, Button, Chip } from '@curves/ui-base';
import { Icons } from '@curves/ui-base';
import { matchCycleOrderStatusColor } from '@Local/CyclePurchase/staticData';
import { EFFECT_CONTENT_TYPE } from '@Constants/dataEnums';
import LoadingText from '@Components/loading/LoadingText';
import { ProductSection, ShippingSection, ReceiverSection, SuspensionSection, TerminateSection, ResumeSection } from './CyclePurchaseEffectSections';

// 展開區 effectType → 變更內容區塊對應（排除 EffectInfoSection，其欄位與卡片上的狀態、生效日、申請日、擔當重複）
const _effectTypeMap = new Map([
    [EFFECT_CONTENT_TYPE.ITEMS,       ProductSection],
    [EFFECT_CONTENT_TYPE.CHANGEITEMS, ProductSection],
    [EFFECT_CONTENT_TYPE.SHIPPINGDAY, ShippingSection],
    [EFFECT_CONTENT_TYPE.RECEIVER,    ReceiverSection],
    [EFFECT_CONTENT_TYPE.STOP,        SuspensionSection],
    [EFFECT_CONTENT_TYPE.QUIT,        TerminateSection],
    [EFFECT_CONTENT_TYPE.RESTOP,      ResumeSection],
]);

/**
 * 預約變更清單
 *
 * record 欄位（對應後端 API）：
 *   effectID, effectStatusText, effectTypeText[], effectCreateDate,
 *   effectDate, effectStatus, effectStatusColor, employeeName, canDeleted, url（PDF）
 *
 * @param {object[]} records
 * @param {object} [expandedIds] { [effectID]: true } 目前展開中的變更單
 * @param {object} [loadingIds] { [effectID]: true } 目前詳情請求 in-flight 的變更單
 * @param {object} [detailMap] { [effectID]: data } 已取得的詳情快取（data 為 effection API 回傳內容）
 * @param {function} onToggleDetail  (record) => void  點擊「查看詳情／收合詳情」，於卡片內就地展開切換
 * @param {function} onDelete  (record) => void  刪除預約變更單（僅 canDeleted 時顯示）
 */
const CyclePurchasePendingChangeList = ({ records = [], expandedIds = {}, loadingIds = {}, detailMap = {}, onToggleDetail, onDelete }) => {
    if (records.length === 0) {
        return (
            <Box className="cp-history-empty">
                <Typography className="cp-history-empty-text">目前沒有預約變更</Typography>
            </Box>
        );
    }

    const toDateStr = (iso) => (iso ? iso.slice(0, 10).replace(/-/g, '/') : '');

    return (
        <Box className="cp-history-list">
            {records.map((record) => {
                const isExpanded = Boolean(expandedIds[record.effectID]);
                const isLoading = Boolean(loadingIds[record.effectID]);
                const detail = detailMap[record.effectID];
                const effectTypes = detail?.effectType ?? [];

                return (
                    <Box key={record.effectID} className="cp-history-card">
                        {/* 標題列：變更項目當主角，狀態 badge 靠右 */}
                        <Box className="cp-history-card-title-row">
                            <Typography className="cp-history-type-text">{record.effectTypeText.join('、')}</Typography>
                            <Chip
                                label={record.effectStatusText}
                                size="small"
                                className="cp-history-badge"
                                color={matchCycleOrderStatusColor(record?.effectStatusColor)}
                            />
                        </Box>

                        {/* 生效日（大字主色） */}
                        <Box className="cp-history-card-body">
                            <Typography className="cp-history-effect-label">生效日</Typography>
                            <Typography className="cp-history-effect-date">{toDateStr(record.effectDate)}</Typography>
                        </Box>

                        {/* footer：申請日 + 擔當 + 查看／收合詳情 */}
                        <Box className="cp-history-card-footer">
                            <Typography className="cp-history-created-at">
                                申請日 {toDateStr(record.effectCreateDate)}
                                {record.employeeName ? `　擔當 ${record.employeeName}` : ''}
                            </Typography>
                            {onToggleDetail && (
                                <Button
                                    variant="text"
                                    size="small"
                                    startIcon={isExpanded ? <Icons.ArrowUp /> : <Icons.ArrowDown />}
                                    onClick={() => onToggleDetail(record)}
                                    className="cp-history-detail-btn"
                                >
                                    {isExpanded ? '收合詳情' : '查看詳情'}
                                </Button>
                            )}
                        </Box>

                        {/* 展開區：卡片內就地顯示變更單編號 + 各變更內容區塊
                            渲染條件含 isLoading —— reducer 在首次展開（無快取）時只設 loadingIds、
                            尚未設 expandedIds，若只看 isExpanded 會導致載入指示完全不顯示 */}
                        {(isExpanded || isLoading) && (
                            <Box className="cp-history-detail">
                                {isLoading && <LoadingText text="載入中" />}
                                {!isLoading && detail && (
                                    <>
                                        <Box className="cp-effect-field">
                                            <Typography className="cp-effect-field-label">變更單編號</Typography>
                                            {/* 空值 fallback 與共用 Field 元件一致，避免同畫面呈現不一致 */}
                                            <Typography className="cp-effect-field-value">{detail.effectNo || '—'}</Typography>
                                        </Box>
                                        {effectTypes.map((type) => {
                                            const Component = _effectTypeMap.get(type);
                                            if (!Component) return null;
                                            return <Component key={type} sourceData={detail} />;
                                        })}
                                    </>
                                )}
                            </Box>
                        )}

                        {/* 刪除：卡片底部全寬按鈕（破壞性動作獨立一排，僅 canDeleted 時顯示） */}
                        {onDelete && record.canDeleted && (
                            <Box className="cp-history-card-actions">
                                <Button fullWidth variant="contained" color="error" startIcon={<Icons.Trash />} onClick={() => onDelete(record)}>
                                    刪除
                                </Button>
                            </Box>
                        )}
                    </Box>
                );
            })}
        </Box>
    );
};

export default CyclePurchasePendingChangeList;
